import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import PlannerToolbar from './PlannerToolbar';
import MetricToggle, { PickupTag } from './MetricToggle';
import { TabBar } from './registerTabs';
import { TODAY_START } from './plannerState';
import { PICKUP_NOTE, isPriced, money, moneyCompact, ratableAt, rateAt } from './revenueLogic';
import ForecastEditor from './ForecastEditor';
import { bucketize, buildUtil, isScenarioView, monthIdx, totals, utilBand, utilPct } from './utilizationLogic';
import { budgetCompare, remainingBand } from './hoursLogic';

const TABS = ['Overview', 'By Resource', 'By Project', 'Budget'];
// The dashed mark is the 65% utilization target (Judy's green threshold), not full capacity.
const TARGET = 0.65;
const MODES = [['allocated', 'Hours allocated'], ['remaining', 'Hours remaining']];

const fmt = (h) => Math.round(h).toLocaleString();
const signed = (h) => (Math.round(h) < 0 ? `−${fmt(Math.abs(h))}` : fmt(h));
const compact = (h) => (Math.abs(h) >= 1000 ? `${(h / 1000).toFixed(1)}k` : String(Math.round(h)));

/** One hours cell. Allocated shades blue by share of capacity; remaining bands by size of the open hours. */
function Hrs({ demand, capacity, n = 1, mode, pickup = 0 }) {
  const pk = pickup > 0 ? <span className="hp-pickup" title={`${fmt(pickup)} h post-go-live pickup (not counted in these hours)`}>$</span> : null;
  if (mode === 'remaining') {
    const rem = capacity - demand;
    const band = remainingBand(rem, n, capacity);
    return <td className={`up-cell hp-${band}`} title={`${fmt(demand)} h allocated of ${fmt(capacity)} h capacity`}>{band === 'none' ? '' : signed(rem)}{pk}</td>;
  }
  const share = capacity > 0 ? demand / capacity : 0;
  return (
    <td className="up-cell up-share" style={demand > 0 ? { background: `rgba(30, 90, 160, ${Math.min(0.55, share * 0.5 + 0.08)})` } : undefined} title={`${fmt(demand)} h allocated of ${fmt(capacity)} h capacity`}>
      {demand > 0 ? fmt(demand) : ''}{pk}
    </td>
  );
}

const Rev = ({ value, pickup = 0 }) => <td className="up-cell up-share" style={value + pickup > 0 ? { background: 'rgba(30, 90, 160, 0.12)' } : undefined} title={money(value + pickup)}>{value + pickup > 0 ? money(value + pickup) : ''}{pickup > 0 && <PickupTag amount={pickup} />}</td>;

export default function HoursPlanner({ canEdit = false, shared, metric, onMetric, rateIndex }) {
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('Overview');
  const [mode, setMode] = useState('allocated');
  const { grain, teamIds, includePipeline, windowMonths, start, scenarios, savedId } = shared;
  const rev = metric === 'revenue';
  const [saved, setSaved] = useState([]);
  const [openTeams, setOpenTeams] = useState(() => new Set());
  const [openProjects, setOpenProjects] = useState(() => new Set());
  const [openBudget, setOpenBudget] = useState(() => new Set());

  const loadFeed = useCallback(() => apiFetch('/capacity-planner').then(setFeed).catch((e) => setError(e.message || 'Could not load the planner.')), []);
  useEffect(() => {
    loadFeed();
    apiFetch('/capacity-planner/views').then((vs) => setSaved(vs.filter((v) => isScenarioView(v.config)))).catch(() => setSaved([]));
  }, [loadFeed]);

  const today = new Date();
  const nowIdx = today.getFullYear() * 12 + today.getMonth();
  // A ratable month's flat amount is spread over that project's planned hours, so teams share it.
  const priceAt = useMemo(() => {
    if (!rev || !rateIndex) return null;
    const hrs = new Map();
    for (const d of feed?.demand || []) { const k = `${d.projectId}|${monthIdx(d.year, d.month)}`; hrs.set(k, (hrs.get(k) || 0) + d.hours); }
    return (pid, i) => {
      const amt = ratableAt(rateIndex, pid, i);
      return amt == null ? rateAt(rateIndex, pid, i) : amt / (hrs.get(`${pid}|${i}`) || Infinity);
    };
  }, [rev, rateIndex, feed]);

  const win = useMemo(() => {
    if (!feed) return null;
    const s = Math.max(0, Math.min(start, feed.months.length - windowMonths));
    return { ...feed, months: feed.months.slice(s, s + windowMonths), people: feed.people.map((p) => ({ ...p, capacity: p.capacity.slice(s, s + windowMonths) })) };
  }, [feed, start, windowMonths]);
  const grid = useMemo(() => win && buildUtil(win, { teamIds, includePipeline, scenarios, nowIdx, priceAt }), [win, teamIds, includePipeline, scenarios, nowIdx, priceAt]);
  const budget = useMemo(() => feed && budgetCompare(feed, { teamIds, includePipeline, scenarios, nowIdx }), [feed, teamIds, includePipeline, scenarios, nowIdx]);

  if (error) return <div className="card"><p className="text-muted">{error}</p></div>;
  if (!feed || !win || (rev && !rateIndex)) return <div className="card"><p className="text-muted">Loading…</p></div>;

  const buckets = (d, c) => bucketize(win, d, c, grain);
  const head = buckets(grid.teams[0]?.demand || [], grid.teams[0]?.capacity || []).map((h) => h.label);
  const all = totals(grid);
  const toggle = (setter, id) => setter((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const Chevron = ({ open }) => <span className="up-chev">{open ? '▾' : '▸'}</span>;
  const cellOf = (b, m, pickup) => (rev ? <Rev key={b.key} value={b.demand} pickup={pickup} /> : <Hrs key={b.key} demand={b.demand} capacity={b.capacity} n={b.n} mode={m} pickup={pickup} />);
  const row = (d, c, pk) => buckets(d, c).map((b, i) => cellOf(b, mode, pk?.[i]));
  const sum = (a) => a.reduce((x, y) => x + y, 0);
  // Pickup is an indicator only (never in the hours above): per-month and per-bucket totals for the markers.
  const pipelineIds = new Set(feed.projects.filter((p) => p.isPipeline).map((p) => p.id));
  // In Revenue the pickup hours are priced at the pickup month's rate; `hours` then holds dollars.
  const pickupList = (feed.pickups || []).filter((p) => includePipeline || !pipelineIds.has(p.projectId)).map((p) => (rev ? { ...p, hours: p.hours * rateAt(rateIndex, p.projectId, p.year * 12 + (p.month - 1)) } : p));
  const pkMonthly = (list) => win.months.map((m) => sum(list.filter((p) => p.year === m.year && p.month === m.month).map((p) => p.hours)));
  const pkBuckets = (list) => buckets(pkMonthly(list), []).map((b) => b.demand);
  const pkAll = pkMonthly(pickupList);
  const pkAllBuckets = pkBuckets(pickupList);
  const yearSpans = [];
  win.months.forEach((m) => { const l = yearSpans[yearSpans.length - 1]; if (l?.year === m.year) l.n += 1; else yearSpans.push({ year: m.year, n: 1 }); });

  const ModeToggle = () => (
    <div className="up-grain" role="group" aria-label="Hours view">
      {MODES.map(([k, label]) => <button key={k} className={mode === k ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setMode(k)}>{label}</button>)}
    </div>
  );

  // ---------- Overview ----------
  let over = 0;
  let open = 0;
  for (const t of grid.teams) for (let k = 0; k < t.demand.length; k += 1) { const rem = t.capacity[k] - t.demand[k]; if (rem < 0) over -= rem; else open += rem; }
  const unnamed = sum(grid.teams.map((t) => sum(t.unassigned)));
  const monthly = win.months.map((_, k) => (mode === 'remaining' ? all.capacity[k] - all.demand[k] : all.demand[k]));
  // 12% headroom so the utilization callout above the tallest bar stays inside the chart.
  const scale = Math.max(1, ...(mode === 'remaining' ? monthly.map(Math.abs) : [...all.demand, ...all.capacity.map((c) => c * TARGET)])) * 1.12;
  const noCapacity = feed.people.filter((p) => (!teamIds.length || teamIds.includes(p.teamId)) && !p.capacity.some(Boolean));
  const allRev = all.demand.map((d, k) => d + (pkAll[k] || 0)); // pickup counts toward revenue
  const revScale = Math.max(1, ...allRev) * 1.12;
  const revOverview = (
    <>
      <div className="up-kpis">
        <div className="up-kpi"><div className="up-kpi-n">{money(sum(allRev))}</div>Revenue in this window (incl. pickup)</div>
        <div className="up-kpi"><div className="up-kpi-n">{money(sum(allRev) / Math.max(1, win.months.length))}</div>Average per month</div>
        {sum(pkAll) > 0 && <div className="up-kpi" title={PICKUP_NOTE}><div className="up-kpi-n" style={{ color: 'var(--accent-orange)' }}>{money(sum(pkAll))}</div>Of which pickup</div>}
      </div>
      <div className="up-split">
        <div className="card">
          <div className="card-title">Revenue by month <span className="text-muted">all teams, hours × billing rate</span></div>
          <div className="up-chart hp-chart">
            {allRev.map((v, k) => (
              <div key={k} className="up-bar-col" title={`${win.months[k].year}-${String(win.months[k].month).padStart(2, '0')}: ${money(v)}`}>
                <div className="up-bar" style={{ height: `${(v / revScale) * 100}%`, background: 'var(--primary-dark)' }}><b>{moneyCompact(v)}</b></div>
                {pkAll[k] > 0 && <div className="hp-pk" title={`${money(pkAll[k])} post-go-live pickup (included in the bar)`}>$</div>}
              </div>
            ))}
          </div>
          <div className="up-axis">{win.months.map((m, k) => <span key={k}>{'JFMAMJJASOND'[m.month - 1]}</span>)}</div>
          <div className="up-years">{yearSpans.map((y) => <span key={y.year} style={{ flex: y.n }}>{y.year}</span>)}</div>
        </div>
        <div className="card">
          <div className="card-title">Teams by {grain} <span className="text-muted">revenue</span></div>
          <div className="up-scroll">
            <table className="up-table">
              <thead><tr><th>Team</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {grid.teams.map((t) => <tr key={t.team.id}><th>{t.team.name}</th>{row(t.demand, t.capacity)}</tr>)}
                <tr className="up-team"><th>All teams</th>{row(all.demand, all.capacity, pkAllBuckets)}</tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
  const overview = (
    <>
      {noCapacity.length > 0 && <div className="up-banner">{noCapacity.length} {noCapacity.length === 1 ? 'person has' : 'people have'} no capacity set (no standard monthly hours, or outside their employment dates) and {noCapacity.length === 1 ? 'is' : 'are'} left out: {noCapacity.map((p) => p.name).join(', ')}.</div>}
      <div className="up-kpis">
        <div className="up-kpi"><div className="up-kpi-n">{fmt(sum(all.demand))}</div>Hours allocated</div>
        <div className="up-kpi"><div className="up-kpi-n">{fmt(sum(all.capacity))}</div>Hours of capacity</div>
        <div className="up-kpi"><div className="up-kpi-n">{fmt(open)}</div>Hours still open</div>
        <div className="up-kpi"><div className="up-kpi-n">{fmt(over)}</div>Hours overbooked</div>
        <div className="up-kpi"><div className="up-kpi-n">{fmt(unnamed)}</div>Team-level hours not yet named</div>
        {sum(pkAll) > 0 && <div className="up-kpi" title="Budget left after forecast, parked in each project's pickup month. Not counted in any hours here."><div className="up-kpi-n"><span className="hp-pickup">$</span> {fmt(sum(pkAll))}</div>Post-go-live pickup hours</div>}
      </div>
      <div className="up-split">
        <div className="card">
          <div className="card-title">{mode === 'remaining' ? 'Hours remaining' : 'Hours allocated'} by month <span className="text-muted">{mode === 'remaining' ? 'all teams' : 'all teams, dashed line = 65% of capacity'}</span></div>
          <div className="up-chart hp-chart">
            {monthly.map((v, k) => (
              <div key={k} className="up-bar-col" title={`${win.months[k].year}-${String(win.months[k].month).padStart(2, '0')}: ${fmt(all.demand[k])} h allocated of ${fmt(all.capacity[k])} h`}>
                <div className={`up-bar ${mode === 'remaining' ? `hp-${v < 0 ? 'over' : remainingBand(v, 1, all.capacity[k])}` : `up-${utilBand(all.demand[k], all.capacity[k])}`}`} style={{ height: `${(Math.abs(v) / scale) * 100}%` }}><b>{mode === 'remaining' ? (v < 0 ? '−' : '') + compact(Math.abs(v)) : compact(v)}</b></div>
                <div className="hp-pct" style={{ bottom: `${(Math.abs(v) / scale) * 100}%` }}>{utilPct(all.demand[k], all.capacity[k]) ?? '—'}%</div>
                {pkAll[k] > 0 && <div className="hp-pk" title={rev ? `${money(pkAll[k])} post-go-live pickup (not counted in the bars)` : `${fmt(pkAll[k])} h post-go-live pickup (not counted in the bars)`}>$</div>}
                {mode === 'allocated' && <div className="hp-cap" style={{ bottom: `${((all.capacity[k] * TARGET) / scale) * 100}%` }} />}
              </div>
            ))}
          </div>
          <div className="up-axis">{win.months.map((m, k) => <span key={k}>{'JFMAMJJASOND'[m.month - 1]}</span>)}</div>
          <div className="up-years">{yearSpans.map((y) => <span key={y.year} style={{ flex: y.n }}>{y.year}</span>)}</div>
        </div>
        <div className="card">
          <div className="card-title">Teams by {grain} <span className="text-muted">{mode === 'remaining' ? 'hours remaining' : 'hours allocated'}</span></div>
          <div className="up-scroll">
            <table className="up-table">
              <thead><tr><th>Team</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {grid.teams.map((t) => <tr key={t.team.id}><th>{t.team.name}</th>{row(t.demand, t.capacity)}</tr>)}
                <tr className="up-team"><th>All teams</th>{row(all.demand, all.capacity, pkAllBuckets)}</tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );

  // ---------- By Resource ----------
  const byResource = (
    <div className="card">
      <div className="up-actions up-pad">
        <button className="btn btn-outline" onClick={() => setOpenTeams(new Set(grid.teams.map((t) => t.team.id)))}>Expand all</button>
        <button className="btn btn-outline" onClick={() => setOpenTeams(new Set())}>Collapse all</button>
      </div>
      <div className="up-scroll">
        <table className="up-table">
          <thead><tr><th>Resource</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {grid.teams.map((t) => {
              const isOpen = openTeams.has(t.team.id);
              return (
                <React.Fragment key={t.team.id}>
                  <tr className="up-team">
                    <th><button className="up-toggle" aria-expanded={isOpen} aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${t.team.name}`} onClick={() => toggle(setOpenTeams, t.team.id)}><Chevron open={isOpen} /> {t.team.name}</button></th>
                    {row(t.demand, t.capacity)}
                  </tr>
                  {isOpen && t.people.filter((p) => p.capacity.some(Boolean)).map((p) => <tr key={p.person.id}><th className="up-indent">{p.person.name}</th>{row(p.demand, p.capacity)}</tr>)}
                  {isOpen && t.unassigned.some(Boolean) && (
                    <tr className="up-unassigned">
                      <th className="up-indent" title="Forecast entered for the team, not yet named to a person">Not yet assigned</th>
                      {buckets(t.unassigned, t.capacity).map((b) => <td key={b.key} className="up-cell up-un">{b.demand ? (rev ? money(b.demand) : `${fmt(b.demand)} h`) : ''}</td>)}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-muted up-note">Team rows count every forecast hour. Resource rows show only hours named to them; “Not yet assigned” is team-level forecast waiting for names, so it is already inside the team's number.</p>
    </div>
  );

  // ---------- By Project ----------
  const nameOf = new Map(feed.projects.map((p) => [p.id, p.name]));
  const projectRows = [...new Set(grid.teams.flatMap((t) => [...t.byProject.keys()]))].sort((a, b) => (nameOf.get(a) || '').localeCompare(nameOf.get(b) || ''));
  const sumArr = (arrs) => (arrs.length ? arrs[0].map((_, k) => arrs.reduce((a, x) => a + x[k], 0)) : []);
  const byProject = (
    <div className="card">
      <div className="up-actions up-pad">
        <button className="btn btn-outline" onClick={() => setOpenProjects(new Set(projectRows))}>Expand all</button>
        <button className="btn btn-outline" onClick={() => setOpenProjects(new Set())}>Collapse all</button>
      </div>
      <div className="up-scroll">
        <table className="up-table">
          <thead><tr><th>Project</th>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {projectRows.map((pid) => {
              const isOpen = openProjects.has(pid);
              const withTeam = grid.teams.filter((t) => t.byProject.has(pid));
              return (
                <React.Fragment key={pid}>
                  <tr className="up-team">
                    <th><button className="up-toggle" aria-expanded={isOpen} aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${nameOf.get(pid)}`} onClick={() => toggle(setOpenProjects, pid)}><Chevron open={isOpen} /> {nameOf.get(pid)}</button></th>
                    {(() => { const pk = pkBuckets(pickupList.filter((x) => x.projectId === pid)); return buckets(sumArr(withTeam.map((t) => t.byProject.get(pid))), sumArr(withTeam.map((t) => t.capacity))).map((b, i) => cellOf(b, 'allocated', pk[i])); })()}
                  </tr>
                  {isOpen && withTeam.map((t) => <tr key={t.team.id}><th className="up-indent">{t.team.name}</th>{buckets(t.byProject.get(pid), t.capacity).map((b) => cellOf(b, 'allocated'))}</tr>)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-muted up-note">Hours allocated to each project, shaded by how much of the team's capacity they take. Project rows shade against the capacity of the teams working on them.</p>
    </div>
  );

  // ---------- Budget ----------
  const pickupOf = new Map((feed.pickups || []).map((p) => [p.projectId, { hours: p.hours, month: new Date(p.year, p.month - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' }) }]));
  const budgetTable = (
    <div className="card">
      <div className="up-actions up-pad">
        <button className="btn btn-outline" onClick={() => setOpenBudget(new Set(budget.map((b) => b.project.id)))}>Expand all</button>
        <button className="btn btn-outline" onClick={() => setOpenBudget(new Set())}>Collapse all</button>
      </div>
      <div className="up-scroll">
        <table className="up-table">
          <thead><tr><th>Project / team</th><th>Budget h</th><th>Logged to date</th><th>Forecast ahead</th><th>Budget left after forecast</th><th>Pickup</th><th>Status</th></tr></thead>
          <tbody>
            {budget.map((b) => {
              const isOpen = openBudget.has(b.project.id);
              const line = (x, label, head) => (
                <tr key={label} className={head ? 'up-team' : undefined}>
                  <th className={head ? undefined : 'up-indent'}>{head ? (
                    <button className="up-toggle" aria-expanded={isOpen} aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${label}`} onClick={() => toggle(setOpenBudget, b.project.id)}><Chevron open={isOpen} /> {label}</button>
                  ) : label}</th>
                  <td className="up-cell">{x.hasBudget ? fmt(x.budget) : '—'}</td>
                  <td className="up-cell">{fmt(x.actual)}</td>
                  <td className="up-cell">{fmt(x.forecast)}</td>
                  <td className={`up-cell ${x.remaining != null && x.remaining < 0 ? 'hp-over' : ''}`}>{x.remaining == null ? '—' : signed(x.remaining)}</td>
                  <td className="up-cell">{head && pickupOf.has(b.project.id) ? <span title={pickupOf.get(b.project.id).month}><span className="hp-pickup">$</span> {fmt(pickupOf.get(b.project.id).hours)} h</span> : ''}</td>
                  <td className="up-cell up-un">{x.remaining == null ? 'Not set' : x.remaining < 0 ? 'Over budget' : ''}</td>
                </tr>
              );
              return (
                <React.Fragment key={b.project.id}>
                  {line(b, b.project.name, true)}
                  {isOpen && b.teams.map((t) => line(t, t.name, false))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-muted up-note">Budget left = budget − hours logged to date − forecast from this month on. This is where forecasting will check a project's plan against both its budget and the team's open capacity.</p>
    </div>
  );

  // Budget and Forecast are hours-only (budget hours, editable forecast hours), so Revenue hides them.
  const tabNames = rev ? TABS.filter((t) => t !== 'Budget') : canEdit ? [...TABS, 'Forecast'] : TABS;
  const shownTab = tabNames.includes(tab) ? tab : 'Overview';
  const changedScenario = Object.values(scenarios).some((x) => x !== 'plan');
  const unpriced = rev ? [...new Set(feed.demand.map((d) => d.projectId))].filter((pid) => !isPriced(rateIndex, pid)).map((pid) => nameOf.get(pid) || pid) : [];

  return (
    <div className="up-page">
      <PlannerToolbar
        teamOptions={feed.teams.map((t) => ({ value: t.id, label: t.name }))} teamIds={teamIds} onTeams={shared.setTeamIds}
        scenarioId={savedId} scenarioLabel={changedScenario && !savedId ? 'Unsaved scenario' : 'Baseline'} saved={saved} onScenario={(id) => shared.pickScenario(id, saved)}
        grain={grain} onGrain={shared.setGrain} includePipeline={includePipeline} onPipeline={shared.setIncludePipeline}
        windowMonths={windowMonths} onWindow={shared.setWindowMonths}
        onEarlier={shared.earlier} onToday={() => shared.setStart(TODAY_START)}
        onLater={() => shared.later(feed.months.length)}
      />
      <MetricToggle metric={metric} onChange={onMetric} />
      <TabBar tabs={tabNames.map((t) => ({ key: t, label: t }))} active={shownTab} onSelect={setTab} />
      {!rev && (shownTab === 'Overview' || shownTab === 'By Resource') && <div style={{ marginBottom: 12 }}><ModeToggle /></div>}
      {rev && unpriced.length > 0 && <div className="up-banner">{unpriced.length} {unpriced.length === 1 ? 'project has' : 'projects have'} no billing rate and show as $0: {unpriced.join(', ')}.</div>}
      {changedScenario && <div className="up-banner">Showing a what-if scenario, not the baseline.</div>}
      {!rev && mode === 'allocated' && shownTab === 'Overview' && (
        <div className="up-legend">
          <span className="up-cell up-good">65%+</span><span className="up-cell up-warn">50–64%</span>
          <span className="up-cell up-low">Under 50% ▼</span><span className="up-cell up-over">Over 100% ▲</span>
          <span className="text-muted">Bar shading = utilization, same bands as the Utilization tab.</span>
        </div>
      )}
      {!rev && mode === 'remaining' && (shownTab === 'Overview' || shownTab === 'By Resource') && (
        <div className="up-legend">
          <span className="up-cell hp-open">Open</span><span className="up-cell hp-thin">Thin (under 16 h a month)</span><span className="up-cell hp-over">Overbooked</span>
          <span className="text-muted">Hours remaining = capacity − allocated.</span>
        </div>
      )}
      {sum(pkAll) > 0 && ['Overview', 'By Project'].includes(shownTab) && (
        <div className="up-legend"><span className="hp-pickup">$</span><span className="text-muted">{rev ? PICKUP_NOTE : 'Post-go-live pickup: budget left after forecast, parked in one month. Shown for reference; not counted in any hours, capacity or utilization.'}</span></div>
      )}
      {shownTab === 'Overview' && (rev ? revOverview : overview)}
      {shownTab === 'By Resource' && byResource}
      {shownTab === 'By Project' && byProject}
      {shownTab === 'Budget' && budgetTable}
      {shownTab === 'Forecast' && canEdit && (savedId
        ? <div className="card"><p className="text-muted">Switch the scenario back to Baseline to edit the forecast.</p></div>
        : <ForecastEditor feed={feed} win={win} teamIds={teamIds} includePipeline={includePipeline} nowIdx={nowIdx} canEdit={canEdit} onSaved={loadFeed} />)}
    </div>
  );
}
